import { RECEIPT_BRAND_NAME, RECEIPT_LABELS } from '../../constants/receiptLabels';
import { RECEIPT_LOGO_URL, SERVICE_BRAND_NAME } from '../../config/brandConfig';

const BORDER_COLOR = '#adbdcf';
const LABEL_BACKGROUND = '#e3e7ec';
const SECTION_BACKGROUND = '#93a6bd';

const displayValue = (value, fallback = '-') => {
  if (value === null || value === undefined || value === '') return fallback;
  return value;
};

const formatPhoneNumber = (value) => {
  const original = String(value ?? '').trim();
  const numbers = original.replace(/[^0-9]/g, '').slice(0, 11);

  if (!numbers) return original;
  if (/^(15|16|18)\d{6}$/.test(numbers)) {
    return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
  }
  if (numbers.startsWith('02')) {
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  }
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  if (numbers.length <= 10) return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
};

const labelStyle = {
  padding: '4px 6px',
  background: LABEL_BACKGROUND,
  color: '#53606b',
  fontSize: '10px',
  fontWeight: 600,
  lineHeight: 1.25,
  textAlign: 'left',
  border: `1px solid ${BORDER_COLOR}`,
  wordBreak: 'keep-all',
};

const valueStyle = {
  minHeight: '22px',
  padding: '4px 6px',
  background: '#fff',
  color: '#25313b',
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: 1.35,
  textAlign: 'left',
  border: `1px solid ${BORDER_COLOR}`,
  overflowWrap: 'anywhere',
};

function BilingualLabel({ ko, en }) {
  return (
    <>
      <span>{ko}</span>
      {en ? <span style={{ marginLeft: '3px', fontSize: '9px', fontWeight: 500 }}>{en}</span> : null}
    </>
  );
}

function FieldBlock({ labelKo, labelEn, value, blank = false, onReveal }) {
  return (
    <td style={{ padding: 0, verticalAlign: 'top' }}>
      <div style={labelStyle}><BilingualLabel ko={labelKo} en={labelEn} /></div>
      <div style={{ ...valueStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
        <span>{displayValue(value, blank ? '' : '-')}</span>
        {onReveal ? (
          <button
            type="button"
            onClick={onReveal}
            style={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: '3px', background: '#fff', color: '#53606b', padding: '1px 5px', fontSize: '9px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
          >
            조회
          </button>
        ) : null}
      </div>
    </td>
  );
}

function AmountRow({ labelKo, labelEn, value, total = false }) {
  return (
    <tr>
      <th scope="row" style={{ ...labelStyle, width: '34%', fontWeight: total ? 700 : 600 }}>
        <BilingualLabel ko={labelKo} en={labelEn} />
      </th>
      <td style={{ ...valueStyle, textAlign: 'right', color: total ? 'var(--brand-secondary)' : '#18212f', fontSize: total ? '16px' : '11px', fontWeight: 700 }}>
        {displayValue(value)}
      </td>
    </tr>
  );
}

function SectionHeading({ children, colSpan = 1 }) {
  return (
    <th
      colSpan={colSpan}
      scope="colgroup"
      style={{ padding: '5px 6px', background: SECTION_BACKGROUND, color: '#fff', border: `1px solid ${BORDER_COLOR}`, fontSize: '10px', fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}
    >
      {children}
    </th>
  );
}

function BusinessFieldRow({ leftLabelKo, leftLabelEn, leftValue, leftBlank = false, rightLabelKo, rightLabelEn, rightValue, rightBlank = false }) {
  return (
    <>
      <tr>
        <th scope="row" style={{ ...labelStyle, width: '25%' }}><BilingualLabel ko={leftLabelKo} en={leftLabelEn} /></th>
        <th scope="row" style={{ ...labelStyle, width: '25%' }}><BilingualLabel ko={rightLabelKo} en={rightLabelEn} /></th>
      </tr>
      <tr>
        <td style={valueStyle}>{displayValue(leftValue, leftBlank ? '' : '-')}</td>
        <td style={valueStyle}>{displayValue(rightValue, rightBlank ? '' : '-')}</td>
      </tr>
    </>
  );
}

function FullWidthBusinessField({ labelKo, labelEn, value, blank = false }) {
  return (
    <>
      <tr><th scope="row" style={labelStyle}><BilingualLabel ko={labelKo} en={labelEn} /></th></tr>
      <tr><td style={valueStyle}>{displayValue(value, blank ? "" : "-")}</td></tr>
    </>
  );
}

export default function ReceiptLayoutV2({
  data,
  viewMode = 'approval',
  onViewModeChange,
  onRevealCardNumber,
}) {
  if (!data) return null;

  const isCancelView = viewMode === 'cancel';
  const transactionDateTime = isCancelView
    ? displayValue(data.cancelDateTime)
    : displayValue(data.approvalDateTime);
  const pspName = displayValue(data.supplierName, RECEIPT_BRAND_NAME);
  const pgName = displayValue(data.cardMerchantName, '');
  const hasDistinctPgName = pgName && pgName !== pspName;

  return (
    <div style={{ background: '#fff', color: '#25313b', fontFamily: "Arial, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif" }}>
      <div style={{ padding: '18px 10px 10px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', borderBottom: `1px solid ${BORDER_COLOR}` }}>
        <div>
          <div style={{ fontSize: '18px', lineHeight: 1.2, fontWeight: 800, color: '#111318', letterSpacing: '-0.02em' }}>{RECEIPT_LABELS.titleKo}</div>
          <div style={{ marginTop: '4px', fontSize: '10px', lineHeight: 1.2, fontWeight: 500, color: '#41536a' }}>{RECEIPT_LABELS.titleEn}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
          <img src={RECEIPT_LOGO_URL} alt={SERVICE_BRAND_NAME} style={{ display: 'block', width: '96px', height: 'auto', objectFit: 'contain' }} />
          {data.isCanceled && onViewModeChange ? (
            <div style={{ display: 'flex', border: `1px solid ${BORDER_COLOR}`, borderRadius: '3px', overflow: 'hidden' }}>
              <button type="button" onClick={() => onViewModeChange('approval')} style={{ border: 0, padding: '4px 8px', background: viewMode === 'approval' ? '#546673' : '#fff', color: viewMode === 'approval' ? '#fff' : '#546673', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>승인</button>
              <button type="button" onClick={() => onViewModeChange('cancel')} style={{ border: 0, borderLeft: `1px solid ${BORDER_COLOR}`, padding: '4px 8px', background: viewMode === 'cancel' ? '#b91c1c' : '#fff', color: viewMode === 'cancel' ? '#fff' : '#b91c1c', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>취소</button>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ padding: '12px 10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }} aria-label="거래정보">
          <tbody>
            <tr>
              <FieldBlock labelKo="카드종류" labelEn="Card Type" value={data.cardBrand} />
              <FieldBlock labelKo="카드번호" labelEn="Card No." value={data.cardNumberMasked} onReveal={onRevealCardNumber} />
            </tr>
            <tr>
              <FieldBlock labelKo={isCancelView ? '취소일시' : '거래일시'} labelEn="Trans Date & Time" value={transactionDateTime} />
              <FieldBlock labelKo="거래처명" value={data.pharmacyName} />
            </tr>
            <tr>
              <FieldBlock labelKo="상품명" labelEn="Item" value={data.itemName} blank />
              <FieldBlock labelKo="구매자명" labelEn="Purchaser" value={data.purchaserName} blank />
            </tr>
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }} aria-label="승인정보">
          <tbody>
            <tr>
              <FieldBlock labelKo="거래유형" labelEn="Trans Type" value={isCancelView ? '취소' : '승인'} />
              <FieldBlock labelKo="할부" labelEn="Installment" value={data.installmentMonth} />
              <FieldBlock labelKo="승인번호" labelEn="Approval No." value={data.approvalNumber} />
            </tr>
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }} aria-label="금액정보">
          <tbody>
            <AmountRow labelKo="과세 금액" labelEn="TAXABLE" value={data.taxableAmount} />
            <AmountRow labelKo="부가세" labelEn="VAT" value={data.vatAmount} />
            <AmountRow labelKo="면세 금액" labelEn="TAX FREE" value={data.taxFreeAmount} />
            <AmountRow labelKo="봉사료" labelEn="TIPS" value={data.serviceAmount} />
            <AmountRow labelKo="합계" labelEn="Total" value={data.totalAmount} total />
          </tbody>
        </table>

        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", marginTop: "8px" }} aria-label="공급자 정보">
          <thead>
            <tr><SectionHeading>공급자정보 Store Info</SectionHeading></tr>
          </thead>
          <tbody>
            <FullWidthBusinessField labelKo="공급자 상호" labelEn="Store Name" value={data.merchantName} />
            <FullWidthBusinessField labelKo="대표자명" labelEn="Representative" value={data.merchantOwnerName} />
            <FullWidthBusinessField labelKo="사업자등록번호" labelEn="Registration No." value={data.merchantBusinessNo} />
            <FullWidthBusinessField labelKo="사업장주소" labelEn="Address" value={data.merchantAddress} />
            <FullWidthBusinessField labelKo="이용문의" labelEn="Help Desk" value={formatPhoneNumber(data.merchantContact)} />
          </tbody>
        </table>

        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", marginTop: "8px" }} aria-label="결제솔루션사 정보">
          <thead>
            <tr><SectionHeading colSpan={2}>결제솔루션사 정보 PSP Info</SectionHeading></tr>
          </thead>
          <tbody>
            <BusinessFieldRow leftLabelKo="솔루션사 상호" leftLabelEn="PSP Name" leftValue={data.supplierName} rightLabelKo="이용문의" rightLabelEn="Help Desk" rightValue={formatPhoneNumber(data.supplierContact)} />
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: '8px' }} aria-label="결제대행사 정보">
          <thead>
            <tr><SectionHeading colSpan={2}>{RECEIPT_LABELS.pgInfo}</SectionHeading></tr>
          </thead>
          <tbody>
            <BusinessFieldRow leftLabelKo="가맹점명" leftLabelEn="Merchant Name" leftValue={data.cardMerchantName} rightLabelKo="승인관련문의" rightLabelEn="Help Desk" rightValue={formatPhoneNumber(data.cardMerchantContact)} />
          </tbody>
        </table>

        <div style={{ padding: '14px 4px 2px', color: '#56636d', fontSize: '10px', fontWeight: 600, lineHeight: 1.55, textAlign: 'center', borderTop: '1px solid ' + BORDER_COLOR }}>
          <div>
            본 거래와 관련한 영수증(신용카드매출전표 등) 상 가맹점명은{' '}
            <span style={{ color: 'var(--brand-secondary)', fontWeight: 800 }}>{pspName}</span>
            {hasDistinctPgName ? (<> 또는 <span style={{ color: 'var(--brand-secondary)', fontWeight: 800 }}>{pgName}</span></>) : null}{' '}
            중 하나로 표기됩니다.
          </div>
          <div style={{ marginTop: '3px' }}>
            The name of the merchant on the receipt related to this transaction is marked as{' '}
            <span style={{ color: 'var(--brand-secondary)', fontWeight: 800 }}>{pspName}</span>
            {hasDistinctPgName ? (<> or <span style={{ color: 'var(--brand-secondary)', fontWeight: 800 }}>{pgName}</span></>) : null}.
          </div>
          <div style={{ marginTop: '12px', padding: '10px 8px', background: '#f1f3f5', border: '1px solid #d7dce0', borderRadius: '4px', color: '#69757f', fontSize: '9.5px', fontWeight: 600, lineHeight: 1.55 }}>
            부가가치세법 제33조, 제36조 및 제46조에 따라 신용카드매출전표 등을<br />
            발급한 경우에는 세금계산서를 발급(교부)할 수 없습니다.
          </div>
        </div>
      </div>
    </div>
  );
}

